import { getApiEndpoint } from "../utils/constants/api";
import { SHOP_SOURCES } from "../utils/constants/shopSources";
import { wfetch } from "../utils/fetch";
import { isUuid } from "../utils/uuid";
import { getCategoryTreeIds } from "./categories.service";

export const getProducts = async (opts) => {
    const { source, category, queryParams } = opts;

    // JCR source
    if (source === SHOP_SOURCES.JCR) {
        const { lang } = opts;

        const res = await wfetch(getApiEndpoint("PRODUCTS", { queryParams: { lang, ...queryParams } }));
        let products = ensureOrderableAll(res?.data?.results);

        if (!category) return products;

        const categoryIds = getCategoryTreeIds(category);

        return filterProductsByCategoryIds(products, categoryIds, source);
    }

    // External sources — always category-scoped (EXTERNAL_PRODUCTS is /categories/{id}/products).
    // Without a resolved category id we cannot build a valid request: encodeURIComponent(undefined)
    // yields the literal string "undefined", which reaches the connector as the category id and
    // commercetools rejects it with 400 "Invalid UUID string: undefined". Bail out with no products.
    const categoryId = category?.["@id"];
    if (!categoryId) return [];

    const res = await wfetch(
        getApiEndpoint("EXTERNAL_PRODUCTS", {
            pathParams: { source, categoryId: encodeURIComponent(categoryId) },
            queryParams: { limit: 1000, ...queryParams },
        }),
    );

    return ensureOrderableAll(res?.data?.results);
};

export const getProduct = async (opts) => {
    const { source, productId } = opts;

    // JCR source
    if (source === SHOP_SOURCES.JCR) {
        const { lang } = opts;
        // Path or uuid, depending on where the link was built (see utils/links.js).
        const query = isUuid(productId) ? { "@jcr:uuid": productId, lang } : { "@path": productId, lang };
        const res = await wfetch(getApiEndpoint("PRODUCTS", { queryParams: query }));

        return ensureOrderable(res?.data?.results?.[0]);
    }

    // External sources
    const res = await wfetch(getApiEndpoint("EXTERNAL_PRODUCT", { pathParams: { source, productId: encodeURIComponent(productId) } }));

    return ensureOrderable(res?.data);
};

export const filterProductsByCategoryIds = (products, categoryIds, source) => {
    if (source !== SHOP_SOURCES.JCR) return products;

    return products.filter((p) => Array.isArray(p.category) && p.category.some((c) => categoryIds.has(c["@id"])));
};

// Reorder a category's products to match the merchandiser's saved sequence and interleave any
// authored banner ("Störer") tiles. The sequence is ordinal: listed products come first in that
// order, banners are inserted at their position, and products not listed keep their original order
// at the end. Banner markers carry `__banner` so the grid renders a promo tile instead of a product
// card; they have no category/price/variation fields, so the sort/filter utils skip them.
export const applyMerchandising = (products, items) => {
    if (!Array.isArray(items) || !Array.isArray(products)) return products;
    const byId = new Map(products.map((p) => [p["@id"], p]));
    const ordered = [];
    const used = new Set();
    let bannerSeq = 0;
    items.forEach((it) => {
        if (!it) return;
        if (it.kind === "banner") {
            ordered.push({ __banner: true, "@id": `__banner-${bannerSeq++}`, link: it.link || "", size: it.size || "regular" });
        } else if (it.kind === "product" && byId.has(it.productId)) {
            ordered.push(byId.get(it.productId));
            used.add(it.productId);
        }
    });
    products.forEach((p) => {
        if (!used.has(p["@id"])) ordered.push(p);
    });
    return ordered;
};

// This is a demo storefront with no real inventory behind it — nothing must ever show as
// "out of stock". Every product (and every variation size) leaves this service with a
// generous stock, which also drives flone's stock badges, add-to-cart buttons and the
// cart's quantity cap. Applied centrally here because all product data flows through
// getProducts/getProduct.
const DEMO_STOCK = 999;

// External/flone products carry variations as arrays; the JCR delivery returns child nodes
// as objects keyed by node name with an "@nodes" list. Normalize stock in either shape.
const ensureSizesOrderable = (size) => {
    if (Array.isArray(size)) return size.map((s) => ({ ...s, stock: s.stock > 0 ? s.stock : DEMO_STOCK }));
    if (size && Array.isArray(size["@nodes"])) {
        const out = { ...size };
        size["@nodes"].forEach((n) => {
            if (out[n]) out[n] = { ...out[n], stock: out[n].stock > 0 ? out[n].stock : DEMO_STOCK };
        });
        return out;
    }
    return size;
};

const ensureVariationsOrderable = (variation) => {
    if (Array.isArray(variation)) return variation.map((v) => ({ ...v, size: ensureSizesOrderable(v.size) }));
    if (variation && Array.isArray(variation["@nodes"])) {
        const out = { ...variation };
        variation["@nodes"].forEach((n) => {
            if (out[n]) out[n] = { ...out[n], size: ensureSizesOrderable(out[n].size) };
        });
        return out;
    }
    return variation;
};

const ensureOrderable = (product) => {
    if (!product || product.__banner) return product;

    return {
        ...product,
        // Cart/wishlist/compare identity: the flone reducers and cards match on `id`,
        // which delivery products don't carry (they use "@id"). Without it every product
        // matches every cart item (undefined === undefined) and one add-to-cart flips
        // the whole grid to "Added".
        id: product.id ?? product["@id"],
        stock: product.stock > 0 ? product.stock : DEMO_STOCK,
        variation: ensureVariationsOrderable(product.variation),
    };
};

const ensureOrderableAll = (products) => (Array.isArray(products) ? products.map(ensureOrderable) : products);
